import ServiceLayout from '@/pages/services/components/ServiceLayout';
import type { ServiceContent } from '@/pages/services/components/ServiceLayout';

export const content: ServiceContent = {
  tag: 'AI Business Improvement',
  title: '業務が変わらなければ、AIを導入しても成果にならない。',
  subtitle: 'まずは一つの業務から、AIで改善する。',
  description:
    'LUVIRAは、企業ごとの業務と課題を確認し、改善効果の高い業務からAIの活用を始めます。',
  icon: 'ri-flashlight-line',
  heroColor: {
    blob1: 'linear-gradient(135deg, #1E40AF, #0EA5E9)',
    blob2: 'linear-gradient(135deg, #3B82F6, #06B6D4)',
    accent: '#38BDF8',
    glow: 'from-blue-400 to-cyan-400',
  },
  aboutTitle: 'AI業務改善とは',
  aboutParagraphs: [
    'AI業務改善とは、企業ごとの業務内容を確認し、改善効果の高い業務からAIを活用するサービスです。業務の流れを見直し、AIを組み込める部分を見つけ、現場で使える形に整えることです。',
    'まずは一つの業務から改善を始めます。改善の効果と現場の反応を確認します。必要に応じて、継続的な運用やシステム開発へ進みます。',
  ],
  challenges: [
    '毎月、同じ集計・転記・報告に時間がかかる。',
    '担当者しか分からない業務が増えている。',
    'AIを導入したいが、対象業務を選べない。',
    'ツールを導入しても、現場に定着するか不安がある。',
    'AIの利用ルールや情報管理をどう決めればよいか分からない。',
    '業務改善に取り組みたいが、何から始めるべきか分からない。',
  ],
  featuresTitle: 'AI業務改善でできること',
  features: [
    {
      icon: 'ri-search-eye-line',
      title: '業務を見つける',
      items: [
        '日々の業務を洗い出す。',
        '時間がかかっている業務を見つける。',
        '繰り返し発生している業務を見つける。',
        '担当者に依存している業務を見つける。',
      ],
    },
    {
      icon: 'ri-lightbulb-line',
      title: '改善方法を考える',
      items: [
        'AIで効率化できる業務を選ぶ。',
        '既存ツールを活用できる方法を考える。',
        '業務の流れを見直す。',
        '人が判断する業務とAIに任せる業務を分ける。',
      ],
    },
    {
      icon: 'ri-robot-line',
      title: 'AIを業務に組み込む',
      items: [
        '文書作成を支援する。',
        '情報の検索と整理を支援する。',
        '集計や報告を効率化する。',
        '問い合わせ対応を支援する。',
        '社内ナレッジの活用を支援する。',
      ],
    },
    {
      icon: 'ri-refresh-line',
      title: '使い続けられる状態をつくる',
      items: [
        '現場が使える運用方法を考える。',
        '利用ルールを整理する。',
        '使い方を共有する。',
        '利用状況を確認する。',
        '必要に応じて改善する。',
      ],
    },
  ],
  valueTitle: '導入後に得られる価値',
  value: [
    '同じ業務にかかる時間を見直せる。',
    '情報を探す時間を減らせる。',
    '担当者への確認を減らせる。',
    '業務の進め方を共有しやすくなる。',
    '経営者が、目の前の確認ではなく次の判断に時間を使いやすくなる。',
    '担当者が、繰り返し作業ではなく重要な業務に集中しやすくなる。',
    'AIを一度導入して終わりではなく、継続的に改善できる。',
  ],
  whyTitle: 'なぜLuviraなのか',
  why: [
    'LUVIRAは、AIツールを導入して終わりにはしません。業務の確認から始めます。改善する業務を一緒に選びます。現場で使える形を考えます。導入後の運用を支援します。必要に応じてシステム開発へつなげます。AIを企業の中で使い続けるための仕組みを考えます。',
  ],
  casesDescription:
    '現在、掲載準備中です。導入事例の公開に向けて、課題・実施内容・変化を確認しています。自社の業務課題については、事例の公開を待たずにご相談いただけます。',
  flow: [
    {
      step: 'STEP 1',
      title: '業務課題を確認する',
      items: [
        '現在の業務内容を把握します。',
        '時間がかかっている業務を洗い出します。',
        '業務上の負担と課題を言語化します。',
      ],
    },
    {
      step: 'STEP 2',
      title: '改善する業務を選ぶ',
      items: [
        '改善効果の高い業務を選定します。',
        'AIの活用方法を検討します。',
        '最初に取り組む範囲を定めます。',
      ],
    },
    {
      step: 'STEP 3',
      title: 'AIを活用して改善する',
      items: [
        '業務に合わせてAIの活用方法を設計します。',
        '現場で使える形に整えます。',
        '実際の業務で使い始めます。',
      ],
    },
    {
      step: 'STEP 4',
      title: '運用しながら見直す',
      items: [
        '利用状況を把握します。',
        '現場の意見を聞き取ります。',
        '必要に応じて運用方法を調整します。',
      ],
    },
    {
      step: 'STEP 5',
      title: '次の改善につなげる',
      items: [
        '改善できた業務と残った課題を振り返ります。',
        '次に取り組む業務を選びます。',
        '必要に応じてAI運用・改善支援やAIシステム開発へ進みます。',
      ],
    },
  ],
  faq: [
    { q: 'AIに詳しくなくても相談できますか？', a: 'はい。AIに詳しくなくても問題ありません。業務内容を丁寧に確認し、分かりやすくご説明します。' },
    { q: 'どの業務から始めればよいか分かりません。', a: '問題ありません。業務内容を確認し、改善効果の高い業務を一緒に選びます。' },
    { q: '大規模なシステム開発が前提ですか？', a: 'いいえ。まずは一つの業務から小さく始めることを基本としています。' },
    { q: '既存のツールは使えますか？', a: 'はい。新しく作る前に、既存ツールを活用できる方法も含めて検討します。' },
    { q: '導入後もサポートしてもらえますか？', a: 'はい。導入後も運用・改善を支援し、使い続けられる状態まで伴走します。' },
    { q: '相談したら契約しなければなりませんか？', a: 'いいえ。まずはご相談いただく段階で、契約は前提ではありません。' },
  ],
  cta: {
    title: 'まずは、一つの業務から改善する。',
    description:
      'AI導入を検討しているものの、何から始めるべきか分からない。その状態からご相談いただけます。LUVIRAが業務内容を確認し、改善効果の高いテーマを一緒に考えます。',
    buttonText: 'AI業務改善について相談する',
    serviceKey: 'AI業務改善',
  },
};

export default function AiImprovementPage() {
  return <ServiceLayout content={content} />;
}